import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import { extractErrorMessage } from '../utils/errorUtils';
import aiService from '../modules/ai/services/aiService';

export default function AiChatDialog({ open, onClose, mode, entityId, entityName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [vectorizing, setVectorizing] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMessages([]);
    setInput('');
    setError('');
    setReady(false);
    setVectorizing(true);
    setConversationId(null);

    const init = async () => {
      try {
        // getOrCreateConversation now also ensures embeddings are generated
        const convRes = await aiService.getOrCreateConversation(mode, entityId);
        const convData = convRes.data?.data;

        if (!convData?.id) throw new Error('No se pudo crear la conversación');
        setConversationId(convData.id);

        if (!convData.isNew) {
          // Load existing messages
          const msgRes = await aiService.getConversationMessages(convData.id);
          const msgs = msgRes.data?.data?.messages || [];
          if (msgs.length > 0) {
            setMessages(msgs.map(m => ({ role: m.role, content: m.content, sources: m.sources })));
            setReady(true);
            return;
          }
        }

        setReady(true);
        setMessages([{
          role: 'assistant',
          content: `Hola, puedes hacerme preguntas sobre ${mode === 'document' ? 'este documento' : 'los documentos de este expediente'}.`,
        }]);
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setVectorizing(false);
      }
    };

    init();
  }, [open, entityId, mode]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || loading || !ready || !conversationId) return;

    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setInput('');
    setLoading(true);

    try {
      const res = await aiService.chatWithConversation(conversationId, question);
      const data = res.data?.data || {};
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.answer || 'Sin respuesta.',
        sources: data.sources,
      }]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Ocurrió un error al procesar tu pregunta. Intenta de nuevo.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="span" noWrap>
          Chat con {entityName}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
        {vectorizing ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Procesando documentos...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {messages.map((msg, idx) => (
              <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Box sx={{
                  maxWidth: '75%', px: 2, py: 1, borderRadius: 2,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  <Typography variant="body2"
                    dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
                  {msg.sources?.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {msg.sources.map((src, i) => (
                        <Chip key={i} label={`${src.fileName} ~p.${src.pageEstimate || '?'}`}
                          size="small" variant="outlined"
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(255,255,255,0.8)' }} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'grey.100' }}>
                  <CircularProgress size={16} />
                </Box>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField fullWidth multiline maxRows={3} size="small"
          placeholder={ready ? 'Escribe tu pregunta...' : 'Procesando...'}
          value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
          disabled={!ready || loading} />
        <IconButton color="primary" onClick={handleSend} disabled={!ready || loading || !input.trim()}>
          <Send />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
}
